import { Typography, Box, useMediaQuery, Grid, Tabs, Tab } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { addToken } from "store/auth/auth.slice";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "router/types";
import ReactGA from "react-ga4";
import React, { useEffect, useState, useContext } from "react";
import { useIntl } from "react-intl";
import SimpleButton from "components/SimpleButton";
import "../../pages/Login/styles.css";
import Password from "components/Password";
import I18nKey from "translations/I18nKey";
import { apiClient } from "api";
import { useQuery } from "react-query";
import { SignUpModal } from "../Login/SignUpModal";
import { CURRENT_ENVIRONMENT, environments } from "constants/constants";
import { AppState } from "store/types";
import InputField from "components/InputField";
import { AlertPopupMobile } from "components/AlertPopupMobile";
import { TourContext } from "contexts/TourContext";
import { changeValue } from "store/SellerOnboarding/sellerOnboarding.slice";
import { LanguageContext } from "contexts/LanguageContext";
import classNames from "classnames";
import useLoginSignupStyles from "../../pages/Login/styles";
import { ConfirmVerifyonLogin } from "../Login/ConfirmVerifyonLogin";
import DialogBox from "components/DialogBox";
import { SignUpConfirmMobile } from "../Login/SignUpConfirmMobile";
import { ProductsModal } from "../Login/ProductsModal";
import useLandingPageLoginSignupStyles from "./styles";

const LandingPageLogin = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [emailInput, setEmailInput] = useState<string>("");
  const [usernameInput, setUserNameInput] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [passwordInput, setPasswordInput] = useState<string>("");
  const [openPasswordMenu, setOpenPasswordMenu] = useState<boolean>(false);
  const [showVerifyAcc, setShowVerifyAcc] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [signupLoading, setSignupLoading] = useState<boolean>(false);
  const [passwordFieldConditions, setPasswordFieldConditions] = useState<
    string[]
  >([]);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [alertMsg, setAlertMsg] = useState<string>("");
  const { user } = useSelector((state: AppState) => state.auth);
  const [err, setErr] = useState<string>("");
  const [enableForgotPassword, setEnableForgotPassword] =
    useState<boolean>(false);
  const [inputFocus, setInputFocus] = useState<boolean>(false);
  const [isOTPSend, setIsOTPSend] = useState<boolean>(false);
  const classes = useLandingPageLoginSignupStyles();
  const originalClasses = useLoginSignupStyles();

  const { showSignUpModal, openProductsModal, handleOpenSignUpModal } =
    useContext(TourContext);
  const { appLocale, setAppLocale } = useContext(LanguageContext);

  const handleModalClose = () => {
    handleOpenSignUpModal(false);
  };

  const handleChangeUsernameInput = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setUserNameInput(e.target.value);
    if (err.length > 0) setErr("");
  };

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordInput(e.target.value);
    if (err.length > 0) setErr("");
  };

  const errorAlert = (
    message: string = intl.formatMessage({
      id: I18nKey.INVALID_EMAIL_PASSWORD,
    })
  ) => {
    setErr(message);
    message = "";
    setEnableForgotPassword(true);
  };

  const getLoginData = async () => {
    setLoading(true);
    await apiClient
      .userLogin(usernameInput, password)
      .then((res: any) => {
        if (res?.data?.data) {
          let accessToken = res.data.data.access_token;
          dispatch(addToken(accessToken));
          navigate(ROUTES.Landing);
          setErr("");
        } else if (res.config.data.data.errors) {
          errorAlert(res.config.data.data.errors);
        }
      })
      .catch((err) => {
        const errResponse = err.response;
        errorAlert();
        if (errResponse.status === 403) {
          if (!errResponse.data?.errors?.is_verified) {
            // For existing and non verified user
            setErr(errResponse.data?.errors?.message);
            setShowVerifyAcc(true);
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const {
    data: loginData,
    refetch: loginRefetch,
    isFetching: isFetchingLogin,
  } = useQuery("userLogin", getLoginData, {
    staleTime: Infinity,
    retry: 1,
    retryDelay: 10000,
    enabled: false,
  });

  useEffect(() => {
    // For Password validation
    const passwordValidation = [];
    if (passwordInput) {
      setOpenPasswordMenu(true);
      if (passwordInput.length < 8)
        passwordValidation.push(
          intl.formatMessage({
            id: I18nKey.PASSWORD_CHARACTERS,
          })
        );
      if (!passwordInput.match(/[A-Z]/g))
        passwordValidation.push(
          intl.formatMessage({
            id: I18nKey.PASSWORD_CAPLETTER,
          })
        );
      if (!passwordInput.match(/[^0-9a-zA-z]/g))
        passwordValidation.push(
          intl.formatMessage({
            id: I18nKey.PASSWORD_SPECIAL_SYMBOL,
          })
        );
      if (!passwordInput.match(/[0-9]/g))
        passwordValidation.push(
          intl.formatMessage({
            id: I18nKey.PASSWORD_NUMBER,
          })
        );
      if (!passwordInput.match(/[a-z]/g))
        passwordValidation.push(
          intl.formatMessage({
            id: I18nKey.PASSWORD_SMALL_LETTER,
          })
        );
      if (!passwordInput.match(/^(?!.*^[^0-9a-zA-Z])/g))
        passwordValidation.push(
          intl.formatMessage({
            id: I18nKey.PASSWORD_NO_SPECIAL_SYMBOL,
          })
        );

      setPasswordFieldConditions(passwordValidation);
    } else {
      passwordValidation.length = 0;
    }
    if (passwordValidation.length === 0) {
      setPassword(passwordInput);

      setOpenPasswordMenu(false);
    }
    // password validation end

    // email validation end
  }, [loginData, passwordInput, password, email, usernameInput]);

  const handleLoginClick = async (e: any) => {
    e.preventDefault();
    if (!password || !usernameInput) {
      setErr(
        intl.formatMessage({
          id: I18nKey.PASSWORD_VALID_CREDENTIALS,
        })
      );
    } else {
      getLoginData();
    }
  };

  const closeOpenMenus = () => {
    setOpenPasswordMenu(false);
  };

  const handleSignIn = async (userName: string, pwd: string) => {
    setLoading(true);
    await apiClient
      .userLogin(userName, pwd)
      .then((res: any) => {
        if (res?.data?.data) {
          let accessToken = res.data.data.access_token;
          dispatch(addToken(accessToken));
          setAlertMsg(
            intl.formatMessage({
              id: I18nKey.SIGNIN_SUCCESS,
            })
          );
          dispatch(changeValue(1));
          navigate(ROUTES.Sellers);
          setErr("");
        } else if (res.config.data.data.errors) {
          setAlertMsg(res.config.data.data.errors);
        }
      })
      .catch((err) => {
        console.log(err);
        setAlertMsg(err.response.data.errors);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSignUp = () => {
    handleOpenSignUpModal(true);
    // Track the event when the signup button is clicked for prod env.
    if (CURRENT_ENVIRONMENT === environments.PRODUCTION) {
      ReactGA.event({
        category: "User",
        action: "Signup Click",
        // label:
        //   user?.user?.companyId !== ridgeWoodId ? "Non ridgewood" : "Ridgewood",
      });
    }
  };

  const handleForgotPasswordClick = () => {
    navigate(ROUTES.ForgotPassword);
    // Track the event when the forgot password button is clicked for prod env.
    if (CURRENT_ENVIRONMENT === environments.PRODUCTION) {
      ReactGA.event({
        category: "User",
        action: "Forgot Password Click",
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === " ") {
      e.preventDefault(); // Prevent space key
    }
  };

  const handleCloseLoginModal = () => {
    setShowVerifyAcc(false);
    setIsOTPSend(false);
  };

  document.addEventListener("mousedown", closeOpenMenus);
  return (
    <>
      <Box
        className={classNames(classes.loginTrialContainer, {
          mobile: isMobile,
        })}
      >
        <Box
          className={classNames(classes.loginOuterContainer, {
            mobile: isMobile,
          })}
        >
          <Box
            className={classNames(originalClasses.loginInnerContainer, {
              mobile: isMobile,
            })}
          >
            <form
              className={classNames(classes.form, {
                mobile: isMobile,
              })}
              method="POST"
              onSubmit={handleLoginClick}
              style={{
                marginTop: err ? "30px" : "10px",
              }}
            >
              <Box
                className={classNames(originalClasses.emailBox, {
                  mobile: isMobile,
                })}
              >
                <InputField
                  value={usernameInput}
                  onChange={handleChangeUsernameInput}
                  placeholder={intl.formatMessage({
                    id: I18nKey.LANDING_PAGE_LOGIN_EMAIL,
                  })}
                  err={err}
                  type="string"
                  className={classNames(classes.email, {
                    mobile: isMobile,
                  })}
                  onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
                    handleKeyDown(e)
                  }
                />
              </Box>
              <Box>
                <Password
                  value={passwordInput}
                  onChange={handleChangePassword}
                  placeholder={intl.formatMessage({
                    id: I18nKey.LANDING_PAGE_LOGIN_PASSWORD,
                  })}
                  conditions={passwordFieldConditions}
                  openMenu={openPasswordMenu}
                  err={err}
                  width={isMobile ? "96%" : "250px"}
                />
              </Box>
              <Box
                className={classNames(classes.buttonsHolder, {
                  mobile: isMobile,
                })}
              >
                <SimpleButton
                  text={intl.formatMessage({
                    id: I18nKey.LOGIN,
                  })}
                  loading={loading}
                  onClick={handleLoginClick}
                  className={classNames(classes.loginBtn, {
                    mobile: isMobile,
                  })}
                />
                <input hidden type="submit" />
                <SimpleButton
                  primary
                  text={intl.formatMessage({
                    id: I18nKey.LANDING_PAGE_LOGIN_SIGNUP,
                  })}
                  loading={signupLoading}
                  onClick={handleSignUp}
                  className={classNames(classes.signUpBtn, {
                    mobile: isMobile,
                  })}
                />
              </Box>
            </form>
          </Box>
          {err && (
            <Box
              className={classNames(classes.forgotPasswordOuterContainer, {
                mobile: isMobile,
              })}
            >
              <Box
                className={classNames(
                  originalClasses.forgotPasswordInnerContainer,
                  {
                    mobile: isMobile,
                  }
                )}
              >
                <Typography
                  onClick={handleForgotPasswordClick}
                  className={classNames(classes.forgotPassword, {
                    mobile: isMobile,
                  })}
                >
                  {intl.formatMessage({
                    id: I18nKey.FORGOT_PASSWORD,
                  })}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {showSignUpModal && (
          <SignUpModal
            isModalOpen={showSignUpModal}
            handleModalClose={handleModalClose}
            setSignupLoading={setSignupLoading}
            signupLoading={signupLoading}
            inputFocus={inputFocus}
            setIsSuccess={setIsSuccess}
            setAlertMsg={setAlertMsg}
            handleSignIn={handleSignIn}
          />
        )}
        {showVerifyAcc && (
          <DialogBox
            isModalOpen={showVerifyAcc}
            handleModalClose={handleCloseLoginModal}
            content={
              !isOTPSend ? (
                <ConfirmVerifyonLogin
                  userName={usernameInput}
                  setConfirmUserName={setUserName}
                  setIsOTPSend={setIsOTPSend}
                />
              ) : (
                <SignUpConfirmMobile
                  userName={userName}
                  password={password}
                  handleModalClose={handleModalClose}
                  setIsSuccess={setIsSuccess}
                  setSignupLoading={setSignupLoading}
                  handleSignIn={handleSignIn}
                  setAlertMsg={setAlertMsg}
                />
              )
            }
          ></DialogBox>
        )}

        {err && isMobile ? (
          <AlertPopupMobile
            loading={isLoading}
            isSuccess={isSuccess}
            alertMsg={err}
            className={classNames(originalClasses.alertPopupMobile, {
              mobile: isMobile,
            })}
          />
        ) : null}
      </Box>
    </>
  );
};

export default LandingPageLogin;
